import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Brain, AlertTriangle, CheckCircle, Clock, HeartPulse } from "lucide-react";

// Helper to clean text that might contain JSON
function cleanText(text) {
  if (!text || typeof text !== 'string') return '';
  
  let cleaned = text.trim();
  
  // Remove markdown code block markers
  if (cleaned.startsWith('```json')) {
    cleaned = cleaned.replace(/^```json\s*/, '').replace(/```\s*$/, '');
  } else if (cleaned.startsWith('```')) {
    cleaned = cleaned.replace(/^```\s*/, '').replace(/```\s*$/, '');
  }
  
  // Try to parse if it looks like JSON
  if (cleaned.startsWith('{') || cleaned.startsWith('[')) {
    try {
      const parsed = JSON.parse(cleaned);
      if (parsed.overall_assessment) return parsed.overall_assessment;
      if (parsed.summary) return parsed.summary;
      if (typeof parsed === 'string') return parsed;
      return '';
    } catch (e) {
      // Not valid JSON
    }
  }
  
  // Clean any JSON remnants
  cleaned = cleaned
    .replace(/^\s*["']?json\s*[{[]/i, '')
    .replace(/[}\]]\s*["']?\s*$/i, '')
    .replace(/"overall_assessment"\s*:\s*"/i, '')
    .replace(/\\n/g, ' ')
    .replace(/\\"/g, '"')
    .replace(/"\s*$/g, '')
    .trim();
  
  return cleaned;
}

const DiagnosisSection = ({ title, content, icon: Icon, color = "slate", children }) => {
  const cleanedContent = cleanText(content);
  if (!cleanedContent && !children) return null;
  
  const colorClasses = {
    slate: "bg-slate-50 border-slate-200 text-slate-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    amber: "bg-amber-50 border-amber-200 text-amber-700",
    red: "bg-red-50 border-red-200 text-red-700",
    blue: "bg-blue-50 border-blue-200 text-blue-700",
    indigo: "bg-indigo-50 border-indigo-200 text-indigo-700",
  };
  
  return (
    <div className={`rounded-md border p-4 ${colorClasses[color]}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={16} />
        <h4 className="font-bold text-sm uppercase tracking-wider">{title}</h4>
      </div>
      {cleanedContent && <p className="text-sm leading-relaxed whitespace-pre-line">{cleanedContent}</p>}
      {children}
    </div>
  );
};

const IssueCard = ({ issue }) => (
  <div className={`border rounded-md p-3 ${
    issue.severity === 'high' ? 'bg-red-50 border-red-200' : 
    issue.severity === 'medium' ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'
  }`}>
    <div className="flex items-center justify-between mb-1">
      <span className="font-semibold text-sm">{issue.component}</span>
      <Badge className={`text-sm ${
        issue.severity === 'high' ? 'bg-red-100 text-red-700' : 
        issue.severity === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700'
      }`}>
        {issue.severity}
      </Badge>
    </div>
    <p className="text-sm text-slate-600 mb-1">{issue.issue}</p>
    {issue.evidence && <p className="text-sm text-slate-500 italic">{issue.evidence}</p>}
    {issue.action && <p className="text-sm text-indigo-600 mt-1 font-medium">{issue.action}</p>}
  </div>
);

const PredictiveWarningCard = ({ warning }) => (
  <div className={`border rounded-md p-3 ${
    warning.risk_level === 'high' ? 'bg-red-50 border-red-200' : 
    warning.risk_level === 'medium' ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200'
  }`}>
    <div className="flex items-center justify-between mb-1">
      <span className="font-semibold text-sm">{warning.component}</span>
      <div className="flex items-center gap-2">
        <Badge className="bg-blue-100 text-blue-700 text-sm">{warning.timeline}</Badge>
        <Badge className={`text-sm ${
          warning.risk_level === 'high' ? 'bg-red-100 text-red-700' : 
          warning.risk_level === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700'
        }`}>
          {warning.risk_level} risk
        </Badge>
      </div>
    </div>
    <p className="text-sm text-slate-600 mb-1">{warning.potential_issue}</p>
    {warning.warning_signs && <p className="text-sm text-amber-600">Signs: {warning.warning_signs}</p>}
    {warning.preventive_action && <p className="text-sm text-emerald-600 mt-1 font-medium">{warning.preventive_action}</p>}
  </div>
);

export default function AIDiagnosisSection({ aiDiagnosis, showPartial = false }) {
  if (!aiDiagnosis || Object.keys(aiDiagnosis).length === 0) {
    return null;
  }
  // Show clean fallback if error
  if (aiDiagnosis.error && !showPartial) {
    return (
      <Card className="border border-slate-200 shadow-none rounded-sm overflow-hidden">
        <div className="border-b border-slate-200 p-4 bg-gradient-to-r from-purple-50 to-pink-50">
          <span className="text-sm font-bold uppercase tracking-wider text-purple-800">AI Predictive Diagnosis</span>
        </div>
        <CardContent className="p-6 text-center">
          <Clock size={32} className="mx-auto text-amber-400 mb-3" />
          <p className="text-base font-medium text-slate-700">AI Diagnosis Temporarily Unavailable</p>
          <p className="text-sm text-slate-400 mt-1">This section will be generated when the AI service is back online. Use the override feature to re-analyse.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border border-slate-200 shadow-none rounded-sm overflow-hidden" data-testid="ai-diagnosis-section">
      <div className="border-b border-slate-200 p-3 bg-gradient-to-r from-purple-50 to-pink-50 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Brain size={16} className="text-purple-600" />
          <span className="text-base font-bold uppercase tracking-wider text-purple-800">
            AI Predictive Diagnosis
          </span>
          <Badge className="bg-purple-100 text-purple-700 border-purple-200 text-sm">
            WISEDRIVE AI ENGINE
          </Badge>
        </div>
        {aiDiagnosis.health_score && (
          <div className="flex items-center gap-2">
            <span className="text-sm font-mono text-purple-600">
              Health Score: {aiDiagnosis.health_score}/100
            </span>
            <Badge className={`text-sm ${
              aiDiagnosis.health_score >= 80 ? 'bg-emerald-100 text-emerald-700' : 
              aiDiagnosis.health_score >= 60 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
            }`}>
              Grade {aiDiagnosis.health_grade}
            </Badge>
          </div>
        )}
      </div>
      
      <CardContent className="p-4 space-y-4">
        {/* Health Score Header */}
        {aiDiagnosis.health_score && (
          <div className="flex items-center justify-between p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-md border border-indigo-200">
            <div>
              <div className="text-sm text-indigo-600 uppercase tracking-wider font-bold mb-1">AI Health Assessment</div>
              <div className="text-2xl font-bold text-indigo-800">
                Score: {aiDiagnosis.health_score}/100 
                <span className="ml-2 text-lg">({aiDiagnosis.health_grade})</span>
              </div>
            </div>
            <HeartPulse size={32} className={`${
              aiDiagnosis.health_score >= 80 ? 'text-emerald-500' : 
              aiDiagnosis.health_score >= 60 ? 'text-amber-500' : 'text-red-500'
            }`} />
          </div>
        )}
        
        {/* Overall Assessment */}
        <DiagnosisSection 
          title="Overall Assessment" 
          content={aiDiagnosis.overall_assessment}
          icon={Brain}
          color={aiDiagnosis.health_score >= 80 ? 'emerald' : 
                 aiDiagnosis.health_score >= 60 ? 'amber' : 'red'}
        />
        
        {/* Current Issues */}
        {aiDiagnosis.current_issues && aiDiagnosis.current_issues.length > 0 && (
          <DiagnosisSection 
            title={`Current Issues (${aiDiagnosis.current_issues.length})`}
            icon={AlertTriangle}
            color="amber"
          >
            <div className="space-y-2 mt-2">
              {aiDiagnosis.current_issues.map((issue, idx) => (
                <IssueCard key={idx} issue={issue} />
              ))}
            </div>
          </DiagnosisSection>
        )}
        
        {/* Predictive Warnings */}
        {aiDiagnosis.predictive_warnings && aiDiagnosis.predictive_warnings.length > 0 && (
          <DiagnosisSection 
            title={`Predictive Warnings (${aiDiagnosis.predictive_warnings.length})`}
            icon={Clock}
            color="blue"
          >
            <div className="space-y-2 mt-2">
              {aiDiagnosis.predictive_warnings.map((warning, idx) => (
                <PredictiveWarningCard key={idx} warning={warning} />
              ))}
            </div>
          </DiagnosisSection>
        )}
        
        {/* Maintenance Priorities */}
        {aiDiagnosis.maintenance_priorities && aiDiagnosis.maintenance_priorities.length > 0 && (
          <DiagnosisSection 
            title="Maintenance Priorities"
            icon={CheckCircle}
            color="slate"
          >
            <div className="space-y-2 mt-2">
              {aiDiagnosis.maintenance_priorities.map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 border border-slate-200 rounded-md p-3 bg-white">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-base font-bold">
                    {item.priority}
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-sm text-slate-800">{item.item}</div>
                    <p className="text-sm text-slate-600">{item.reason}</p>
                    {item.deadline && (
                      <Badge className="mt-1 bg-indigo-100 text-indigo-700 text-sm">
                        Due: {item.deadline}
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </DiagnosisSection>
        )}
        
        {/* Positive Findings */}
        {aiDiagnosis.positive_findings && aiDiagnosis.positive_findings.length > 0 && (
          <DiagnosisSection 
            title="Healthy Components"
            icon={CheckCircle}
            color="emerald"
          >
            <div className="flex flex-wrap gap-2 mt-2">
              {aiDiagnosis.positive_findings.map((finding, idx) => (
                <Badge key={idx} className="bg-emerald-100 text-emerald-700 border-emerald-200 text-sm">
                  <CheckCircle size={10} className="mr-1" /> {finding}
                </Badge>
              ))}
            </div>
          </DiagnosisSection>
        )}
        
        {/* Detailed Explanation */}
        <DiagnosisSection 
          title="Detailed Technical Explanation" 
          content={aiDiagnosis.detailed_explanation}
          icon={Brain}
          color="indigo"
        />
      </CardContent>
    </Card>
  );
}
